import { Dispatch } from 'redux';

import * as api from '../utils/api';
import { notifyError, notifySaveSuccess, notifySuccess } from '../utils/functions';

interface SettingsState {
  data?: ISettings;
  platformData?: ISettings;
}

const actions = {
  UPDATE_SETTINGS: 'lms-ui/settings/UPDATE',
  GET_SETTINGS: 'lms-ui/settings/GET',
  GET_PLATFORM_SETTINGS: 'lms-ui/settings/GET_PLATFORM',
};

interface SettingsAction {
  type:
    | typeof actions.UPDATE_SETTINGS
    | typeof actions.GET_SETTINGS
    | typeof actions.GET_PLATFORM_SETTINGS;
  payload: ISettings;
}

type SettingsActionTypes = SettingsAction;

const initialState: SettingsState = {
  data: undefined,
  platformData: undefined,
};

export const getSettings = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getSettings();
    const settings = response.data as ISettings;
    dispatch({ type: actions.GET_SETTINGS, payload: settings });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const getPlatformSettings = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getPlatformSettings();
    const settings = response.data as ISettings;
    dispatch({ type: actions.GET_PLATFORM_SETTINGS, payload: settings });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

const getFormData = (settings: ISettings, isPlatform: boolean) => {
  const formData = new FormData();
  const props = ['notices', 'maintenance'];

  if (!isPlatform) {
    props.push('passwordExpirationSpan', 'marmonTeams');
  }

  props.forEach((p) => {
    const value = settings[p];
    if (value) {
      switch (p) {
        case 'notices':
          value?.forEach((v: INotice, i: number) => {
            Object.keys(v)
              .filter((k) => ['_id', 'value'].includes(k))
              .forEach((k) => {
                if (v[k]) {
                  formData.append(`${p}[${i}][${k}]`, v[k]);
                }
              });
          });
          break;
        case 'maintenance':
          Object.keys(value).forEach((k) => {
            let data = value[k];
            if (data && k === 'start') {
              data = new Date(data).toISOString();
            }
            formData.append(`${p}[${k}]`, data);
          });
          break;
        case 'marmonTeams':
          value?.forEach((id: string, i: number) => {
            formData.append(`${p}[${i}]`, id);
          });
          break;
        default:
          formData.append(p, value.toString());
          break;
      }
    }
  });

  return formData;
};

export const updateSettings =
  (settings: ISettings, isPlatform: boolean) => async (dispatch: Dispatch) => {
    try {
      const formData = getFormData(settings, isPlatform);
      let response;
      if (isPlatform) {
        response = await api.updatePlatformSettings(formData);
      } else {
        response = await api.updateSettings(formData);
      }

      const updatedSettings = response.data as ISettings;
      dispatch({ type: actions.UPDATE_SETTINGS, payload: updatedSettings });
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const reloadSSOConfigs = () => async (dispatch: Dispatch) => {
  try {
    await api.reloadSSOConfigs();
    notifySuccess('SSO Configs Reloaded');
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export default function reducer(state = initialState, action: SettingsActionTypes): SettingsState {
  switch (action.type) {
    case actions.UPDATE_SETTINGS:
    case actions.GET_SETTINGS: {
      const settings = action.payload as ISettings;
      return {
        ...state,
        data: settings,
      };
    }
    case actions.GET_PLATFORM_SETTINGS: {
      const settings = action.payload as ISettings;
      return {
        ...state,
        platformData: settings,
      };
    }
    default:
      return state;
  }
}
